import { useContext, useEffect, useState } from 'react';

import { postIncodeVerificationUrl, postSaleVerifications } from 'PrivateApis';

import { ConfigContext } from 'Context/config';
import { UserContext } from 'Context/user';
import { VehicleLookupContext } from 'Context/vehicleLookup';

import { APIResponseStatus } from 'Services/api.types';

import { useVehicleCollectionSub } from 'Containers/app/vehicleCollection/hooks/useVehicleCollectionSub';

import { INCODE_ANALYTICS } from '../IncodeAnalytics';
import { extractIncodeSession, IncodeSession, IncodeSessionAPIResponse, IncodeStepProps, SENTRY_TAG, V1IncodeSessionAPIResponse } from '../IncodeSDKHelpers';

export type IncodeSessionState = {
  session: IncodeStepProps['incodeSession'] | undefined;
  success: boolean | undefined;
};

export const useIncodeVerificationSession = () => {
  const { userState } = useContext(UserContext);
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);
  const { collectionDetails, offer, offerStatus } = useVehicleCollectionSub();
  const { configState: { featureFlags } } = useContext(ConfigContext);

  const [incodeSession, setIncodeSession] = useState<IncodeSessionState>({ session: undefined, success: undefined });

  const { auth_token: authToken } = userState ?? {};
  const { dpEnquiryId } = vehicle?.enquiry ?? {};
  const saleId = collectionDetails?.data?.saleId;
  const isOnFinance = !!offer?.data?.finance?.isVehicleOnFinance;
  const useV2 = !!featureFlags?.useV2SaleVerifications;

  useEffect(() => {
    const abortController = new AbortController();

    if (offerStatus !== APIResponseStatus.SUCCESS) {
      return () => {};
    }

    if (!authToken || !dpEnquiryId) {
      setIncodeSession({ session: undefined, success: false });
      return () => {};
    }

    const request = useV2
      ? postSaleVerifications({ Authorization: authToken }, { enquiryId: dpEnquiryId, saleId }, abortController)
      : postIncodeVerificationUrl(dpEnquiryId, { Authorization: authToken }, { isOnFinance }, abortController);

    request
      .then((res: IncodeSessionAPIResponse | V1IncodeSessionAPIResponse) => {
        const sessionData = extractIncodeSession(res);

        if (!sessionData) {
          throw new Error('No incode session returned');
        }

        const { onboardingToken, ...rest } = sessionData;
        const session: IncodeSession = { token: onboardingToken, ...rest };

        INCODE_ANALYTICS.INCODE_ADD_CONTEXT(session);
        setIncodeSession({ session, success: true });
      })
      .catch((err) => {
        globalThis?.Sentry?.captureException?.(err, SENTRY_TAG);
        setIncodeSession({ session: undefined, success: false });
      });

    return () => {
      abortController?.abort();
    };
  }, [authToken, dpEnquiryId, isOnFinance, offerStatus, saleId, useV2]);

  return { incodeSession };
};
